import { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const emptyForm = { material:'', thickness:'', size:'', qty:'', safe_qty:'', memo:'' }

/** 자주 쓰는 재질 — 버튼으로 선택 */
const MATERIAL_PRESETS = ['SS400', 'SUS304', 'EGI', 'AL', 'HR']

/** 자주 쓰는 규격 — 버튼으로 선택 */
const SIZE_PRESETS = [
  { label: '넉자 (4×8)', value: '1219x2438' },
  { label: '다섯자 (5×10)', value: '1524x3048' },
]

/** 보관위치 — 버튼으로 선택 */
const LOCATION_PRESETS = ['용접동', '레이저동', '3공장']

/** 자재 이름 한 줄 표기 */
function matLabel(m) {
  return [m.material, m.thickness, m.size].filter(Boolean).join(' · ')
}

export default function Inventory({ profile }) {
  const [list, setList] = useState([])
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [move, setMove] = useState(null)   // { mat, type:'in'|'out'|'adjust' }
  const [moveQty, setMoveQty] = useState('')
  const [moveNote, setMoveNote] = useState('')
  const [logs, setLogs] = useState(null)   // 이력 보기 대상 mat
  const [logRows, setLogRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [collapsed, setCollapsed] = useState({}) // 재질별 접힘 상태
  const toggleGroup = mat => setCollapsed(c => ({ ...c, [mat]: !c[mat] }))

  useEffect(() => { fetchList() }, [])

  async function fetchList() {
    const { data, error } = await supabase.from('materials').select('*').order('material').order('thickness')
    if (error) console.error('materials 조회:', error.message)
    if (data) setList(data)
  }

  function openNew() {
    setEditingId(null); setForm(emptyForm); setShowForm(true)
  }
  function openEdit(m) {
    setEditingId(m.id)
    setForm({ material:m.material||'', thickness:m.thickness||'', size:m.size||'', qty:String(m.qty), safe_qty:String(m.safe_qty), memo:m.memo||'' })
    setShowForm(true)
  }

  async function submitForm() {
    if (!form.material.trim()) { alert('재질을 입력하세요'); return }
    setLoading(true)
    const payload = {
      material: form.material.trim(),
      thickness: form.thickness.trim(),
      size: form.size.trim(),
      qty: Math.max(0, parseInt(form.qty) || 0),
      safe_qty: Math.max(0, parseInt(form.safe_qty) || 0),
      memo: form.memo,
    }
    const q = editingId
      ? supabase.from('materials').update(payload).eq('id', editingId)
      : supabase.from('materials').insert(payload)
    const { error } = await q
    if (error) {
      alert('저장 실패: ' + error.message + '\n\nSupabase에 materials 테이블이 있는지 확인하세요 (supabase/sql/materials.sql 실행)')
      setLoading(false); return
    }
    setShowForm(false); fetchList(); setLoading(false)
  }

  async function deleteMat(m) {
    if (!window.confirm(`${matLabel(m)} 품목을 삭제할까요?\n입출고 이력도 함께 삭제됩니다.`)) return
    const { error } = await supabase.from('materials').delete().eq('id', m.id)
    if (error) { alert('삭제 실패: ' + error.message); return }
    fetchList()
  }

  function openMove(mat, type) {
    setMove({ mat, type })
    setMoveQty(type === 'adjust' ? String(mat.qty) : '')
    setMoveNote('')
  }

  async function submitMove() {
    const { mat, type } = move
    const n = parseInt(moveQty)
    if (!Number.isFinite(n) || (type !== 'adjust' && n <= 0)) { alert('수량을 입력하세요'); return }

    let delta, qtyAfter
    if (type === 'in') { delta = n; qtyAfter = mat.qty + n }
    else if (type === 'out') {
      if (n > mat.qty && !window.confirm(`현재 재고(${mat.qty}매)보다 많이 출고합니다. 재고는 0으로 처리됩니다. 진행할까요?`)) return
      delta = -Math.min(n, mat.qty); qtyAfter = Math.max(0, mat.qty - n)
    } else { // adjust: 실사 수량으로 맞춤
      if (n < 0) { alert('0 이상으로 입력하세요'); return }
      qtyAfter = n; delta = n - mat.qty
    }

    setLoading(true)
    const { error: upErr } = await supabase.from('materials').update({ qty: qtyAfter }).eq('id', mat.id)
    if (upErr) { alert('저장 실패: ' + upErr.message); setLoading(false); return }
    await supabase.from('material_logs').insert({
      material_id: mat.id, delta, reason: type, qty_after: qtyAfter,
      note: moveNote, created_by_name: profile.name,
    })
    setMove(null); fetchList(); setLoading(false)
  }

  async function openLogs(mat) {
    setLogs(mat); setLogRows([])
    const { data } = await supabase.from('material_logs').select('*').eq('material_id', mat.id).order('created_at', { ascending: false }).limit(100)
    if (data) setLogRows(data)
  }

  const filtered = list.filter(m => {
    const q = search.toLowerCase()
    if (!q) return true
    return matLabel(m).toLowerCase().includes(q)
  })
  const shortageCount = list.filter(m => m.qty <= m.safe_qty).length

  // 재질별 그룹 (프리셋 순 → 기타 가나다순)
  const groupMap = {}
  filtered.forEach(m => { (groupMap[m.material] = groupMap[m.material] || []).push(m) })
  const groupOrder = Object.keys(groupMap).sort((a, b) => {
    const ia = MATERIAL_PRESETS.indexOf(a), ib = MATERIAL_PRESETS.indexOf(b)
    if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
    return a.localeCompare(b, 'ko')
  })

  const inputStyle = { width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }
  const labelStyle = { display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }
  const reasonLabel = { in:'입고', out:'출고', adjust:'실사보정' }
  const moveTitle = { in:'입고 (＋)', out:'출고 (－)', adjust:'실사 보정' }

  function renderCard(m) {
    const short = m.qty <= m.safe_qty
    return (
      <div key={m.id} style={{
        background:'#fff', border:'1px solid ' + (short ? '#f0c0c0' : '#e5e3de'), borderRadius:12, padding:16,
        boxShadow:'0 1px 3px rgba(0,0,0,0.06)',
      }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8, marginBottom:12 }}>
          <div style={{ minWidth:0 }}>
            {/* 두께·규격을 크게 — 한눈에 식별 */}
            <div style={{ display:'flex', alignItems:'baseline', gap:8, flexWrap:'wrap' }}>
              {m.thickness && <span style={{ fontSize:20, fontWeight:800, color:'#1a1916' }}>{m.thickness}</span>}
              {m.size && <span style={{ fontSize:15, fontWeight:700, color:'#3a3833' }}>{m.size}</span>}
              {!m.thickness && !m.size && <span style={{ fontSize:15, color:'#a09e99' }}>규격 미지정</span>}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:4 }}>
              <span style={{ fontSize:11, fontWeight:600, color:'#6b6860', background:'#f0eeea', padding:'2px 8px', borderRadius:5 }}>{m.material}</span>
              {m.memo && <span style={{ fontSize:11, color:'#1e4a8c', background:'#e8eef8', padding:'2px 8px', borderRadius:5 }}>📍 {m.memo}</span>}
            </div>
          </div>
          {short && (
            <span style={{ background:'#fdeaea', color:'#b83030', fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:20, flexShrink:0, whiteSpace:'nowrap' }}>부족</span>
          )}
        </div>

        <div style={{ display:'flex', alignItems:'baseline', gap:6, marginBottom:12 }}>
          <span style={{ fontSize:30, fontWeight:800, color: short ? '#b83030' : '#2d5a3d', lineHeight:1 }}>{m.qty}</span>
          <span style={{ fontSize:14, color:'#6b6860' }}>매</span>
          <span style={{ fontSize:11, color:'#a09e99', marginLeft:'auto' }}>안전 {m.safe_qty}매</span>
        </div>

        <div style={{ display:'flex', gap:8, marginBottom:8 }}>
          <button onClick={() => openMove(m, 'in')}
            style={{ flex:1, padding:'10px 0', border:'none', borderRadius:8, background:'#e8f2eb', color:'#2d5a3d', cursor:'pointer', fontSize:14, fontWeight:700 }}>＋ 입고</button>
          <button onClick={() => openMove(m, 'out')}
            style={{ flex:1, padding:'10px 0', border:'none', borderRadius:8, background:'#fdeaea', color:'#b83030', cursor:'pointer', fontSize:14, fontWeight:700 }}>－ 출고</button>
        </div>
        <div style={{ display:'flex', gap:10, fontSize:11, color:'#a09e99' }}>
          <button onClick={() => openMove(m, 'adjust')} style={{ border:'none', background:'transparent', color:'#6b6860', cursor:'pointer', fontSize:11, padding:0 }}>📋 실사보정</button>
          <button onClick={() => openLogs(m)} style={{ border:'none', background:'transparent', color:'#6b6860', cursor:'pointer', fontSize:11, padding:0 }}>🕘 이력</button>
          <button onClick={() => openEdit(m)} style={{ border:'none', background:'transparent', color:'#6b6860', cursor:'pointer', fontSize:11, padding:0 }}>✏️ 수정</button>
          <button onClick={() => deleteMat(m)} style={{ border:'none', background:'transparent', color:'#b83030', cursor:'pointer', fontSize:11, padding:0, marginLeft:'auto' }}>삭제</button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700, marginBottom:4 }}>원자재 재고 <span style={{ fontSize:13, fontWeight:400, color:'#a09e99' }}>(철판 · 매 단위)</span></div>
          <div style={{ fontSize:13, color:'#a09e99' }}>
            전체 {list.length}종
            {shortageCount > 0 && <span style={{ color:'#b83030', fontWeight:600 }}> · 부족 {shortageCount}종</span>}
          </div>
        </div>
        <button onClick={openNew}
          style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
          + 자재 등록
        </button>
      </div>

      <div style={{ marginBottom:16 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="재질, 두께, 규격 검색..." style={inputStyle} />
      </div>

      {filtered.length === 0 ? (
        <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, padding:50, textAlign:'center', color:'#a09e99' }}>
          <div style={{ fontSize:32, marginBottom:8 }}>🪨</div>
          <div style={{ fontSize:13 }}>+ 자재 등록으로 철판 품목을 추가하세요 (예: SS400 · 3.2t · 1219x2438)</div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {groupOrder.map(mat => {
            const items = groupMap[mat]
            const total = items.reduce((s, m) => s + m.qty, 0)
            const groupShort = items.filter(m => m.qty <= m.safe_qty).length
            const open = !collapsed[mat]
            return (
              <div key={mat} style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:12, overflow:'hidden' }}>
                <div onClick={() => toggleGroup(mat)}
                  style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 16px', cursor:'pointer', background:'#f9f8f6', userSelect:'none' }}>
                  <span style={{ fontSize:13, color:'#6b6860', width:14 }}>{open ? '▾' : '▸'}</span>
                  <span style={{ fontSize:16, fontWeight:700 }}>{mat}</span>
                  <span style={{ fontSize:12, color:'#a09e99' }}>{items.length}종 · 총 {total}매</span>
                  {groupShort > 0 && (
                    <span style={{ background:'#fdeaea', color:'#b83030', fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:20, marginLeft:'auto' }}>
                      부족 {groupShort}
                    </span>
                  )}
                </div>
                {open && (
                  <div style={{ padding:14, display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px,1fr))', gap:12 }}>
                    {items.map(renderCard)}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* 입출고/보정 모달 */}
      {move && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setMove(null)}>
          <div style={{ background:'#fff', borderRadius:14, width:360, maxWidth:'95vw', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontSize:15, fontWeight:700 }}>{moveTitle[move.type]}</div>
                <div style={{ fontSize:12, color:'#a09e99', marginTop:2 }}>{matLabel(move.mat)} · 현재 {move.mat.qty}매</div>
              </div>
              <button onClick={() => setMove(null)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:'20px 24px' }}>
              <label style={labelStyle}>{move.type === 'adjust' ? '실제 재고 수량 (매)' : '수량 (매)'}</label>
              <input type="number" min={0} value={moveQty} onChange={e => setMoveQty(e.target.value)} autoFocus
                onKeyDown={e => e.key === 'Enter' && submitMove()}
                style={{ ...inputStyle, fontSize:20, fontWeight:700, textAlign:'center', padding:'12px' }} />
              {/* 빠른 추가 버튼 */}
              {move.type !== 'adjust' && (
                <div style={{ display:'flex', gap:6, marginTop:10 }}>
                  {[1, 5, 10, 50].map(n => (
                    <button key={n} onClick={() => setMoveQty(String((parseInt(moveQty) || 0) + n))}
                      style={{ flex:1, padding:'7px 0', border:'1px solid #e5e3de', borderRadius:6, background:'#fff', cursor:'pointer', fontSize:12 }}>+{n}</button>
                  ))}
                </div>
              )}
              <div style={{ marginTop:14 }}>
                <label style={labelStyle}>메모 (선택)</label>
                <input value={moveNote} onChange={e => setMoveNote(e.target.value)}
                  placeholder={move.type === 'in' ? '입고처 등' : move.type === 'out' ? '투입 프로젝트 등' : '보정 사유'} style={inputStyle} />
              </div>
              {move.type !== 'adjust' && Number.isFinite(parseInt(moveQty)) && parseInt(moveQty) > 0 && (
                <div style={{ marginTop:12, fontSize:13, color:'#6b6860', textAlign:'center' }}>
                  변동 후 재고: <b style={{ color:'#2d5a3d' }}>
                    {move.type === 'in' ? move.mat.qty + parseInt(moveQty) : Math.max(0, move.mat.qty - parseInt(moveQty))}매
                  </b>
                </div>
              )}
            </div>
            <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setMove(null)}
                style={{ padding:'8px 16px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>취소</button>
              <button onClick={submitMove} disabled={loading}
                style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
                {loading ? '처리 중...' : '확정'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 자재 등록/수정 모달 */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div style={{ background:'#fff', borderRadius:14, width:420, maxWidth:'95vw', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:15, fontWeight:700 }}>{editingId ? '자재 수정' : '자재 등록'}</span>
              <button onClick={() => setShowForm(false)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:'20px 24px' }}>
              <div style={{ marginBottom:14 }}>
                <label style={labelStyle}>재질 *</label>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:8 }}>
                  {MATERIAL_PRESETS.map(mat => {
                    const on = form.material === mat
                    return (
                      <button key={mat} type="button" onClick={() => setForm({...form, material:mat})}
                        style={{
                          padding:'8px 16px', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:700, fontFamily:'inherit',
                          border: on ? '2px solid #2d5a3d' : '1px solid #e5e3de',
                          background: on ? '#e8f2eb' : '#fff',
                          color: on ? '#2d5a3d' : '#6b6860',
                        }}>
                        {mat}
                      </button>
                    )
                  })}
                </div>
                <input value={form.material} onChange={e => setForm({...form, material:e.target.value})}
                  placeholder="직접 입력 (예: 아연도금, 칼라강판)" style={inputStyle} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  <label style={labelStyle}>두께</label>
                  <input value={form.thickness} onChange={e => setForm({...form, thickness:e.target.value})} placeholder="예) 3.2t" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>규격</label>
                  <input value={form.size} onChange={e => setForm({...form, size:e.target.value})} placeholder="예) 1219x2438" style={inputStyle} />
                </div>
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14, marginTop:-4 }}>
                {SIZE_PRESETS.map(s => {
                  const on = form.size === s.value
                  return (
                    <button key={s.value} type="button" onClick={() => setForm({...form, size:s.value})}
                      style={{
                        padding:'7px 14px', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:600, fontFamily:'inherit',
                        border: on ? '2px solid #2d5a3d' : '1px solid #e5e3de',
                        background: on ? '#e8f2eb' : '#fff',
                        color: on ? '#2d5a3d' : '#6b6860',
                      }}>
                      {s.label}
                    </button>
                  )
                })}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  <label style={labelStyle}>{editingId ? '현재 재고 (매)' : '초기 재고 (매)'}</label>
                  <input type="number" min={0} value={form.qty} onChange={e => setForm({...form, qty:e.target.value})} placeholder="0" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>안전재고 (매)</label>
                  <input type="number" min={0} value={form.safe_qty} onChange={e => setForm({...form, safe_qty:e.target.value})} placeholder="0" style={inputStyle} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>보관위치</label>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:8 }}>
                  {LOCATION_PRESETS.map(loc => {
                    const on = form.memo === loc
                    return (
                      <button key={loc} type="button" onClick={() => setForm({...form, memo: on ? '' : loc})}
                        style={{
                          padding:'8px 16px', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:600, fontFamily:'inherit',
                          border: on ? '2px solid #1e4a8c' : '1px solid #e5e3de',
                          background: on ? '#e8eef8' : '#fff',
                          color: on ? '#1e4a8c' : '#6b6860',
                        }}>
                        📍 {loc}
                      </button>
                    )
                  })}
                </div>
                <input value={form.memo} onChange={e => setForm({...form, memo:e.target.value})} placeholder="직접 입력 가능" style={inputStyle} />
              </div>
              {editingId && (
                <div style={{ fontSize:11, color:'#a09e99', marginTop:8 }}>※ 여기서 재고를 직접 고치면 이력에 남지 않습니다. 입출고는 카드의 ＋/－ 버튼을 쓰세요.</div>
              )}
            </div>
            <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setShowForm(false)}
                style={{ padding:'8px 16px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>취소</button>
              <button onClick={submitForm} disabled={loading}
                style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
                {loading ? '저장 중...' : '저장하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 이력 모달 */}
      {logs && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setLogs(null)}>
          <div style={{ background:'#fff', borderRadius:14, width:460, maxWidth:'95vw', maxHeight:'85vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, background:'#fff' }}>
              <div>
                <div style={{ fontSize:15, fontWeight:700 }}>입출고 이력</div>
                <div style={{ fontSize:12, color:'#a09e99', marginTop:2 }}>{matLabel(logs)}</div>
              </div>
              <button onClick={() => setLogs(null)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:'8px 0' }}>
              {logRows.length === 0 ? (
                <div style={{ padding:30, textAlign:'center', color:'#a09e99', fontSize:13 }}>이력이 없습니다</div>
              ) : logRows.map(l => (
                <div key={l.id} style={{ padding:'12px 24px', borderBottom:'1px solid #f0eeea', display:'flex', alignItems:'center', gap:12 }}>
                  <span style={{
                    fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:6, flexShrink:0, minWidth:54, textAlign:'center',
                    background: l.reason==='in' ? '#e8f2eb' : l.reason==='out' ? '#fdeaea' : '#eef0f4',
                    color: l.reason==='in' ? '#2d5a3d' : l.reason==='out' ? '#b83030' : '#5a6270',
                  }}>{reasonLabel[l.reason]}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:14, fontWeight:600 }}>
                      {l.delta > 0 ? '+' : ''}{l.delta}매 <span style={{ fontSize:12, fontWeight:400, color:'#a09e99' }}>→ {l.qty_after}매</span>
                    </div>
                    <div style={{ fontSize:11, color:'#a09e99', marginTop:1 }}>
                      {new Date(l.created_at).toLocaleString('ko-KR', { month:'numeric', day:'numeric', hour:'2-digit', minute:'2-digit' })}
                      {l.created_by_name ? ' · ' + l.created_by_name : ''}
                      {l.note ? ' · ' + l.note : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
